import type { PoaCompleteOutcome } from '../../poa-flow'
import type { CrateItem, MissingCrateItem, OrderDetails, ZebraBarcodesScannedDetails } from '@/entities/order/model/order.types'
import type { PoaSubmission } from '@/features/order/model'
import type { CrateType } from '@/features/order/model/orderDetails.types'
import { Box, Button, Center, Flex, LoadingOverlay, Modal, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconChevronLeft, IconQrcode, IconSettings, IconX } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { clsx } from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAcknowledgeOrder } from '@/entities/order/lib'
import {
  orderKeys,
  useOrderStatusMutation,
  usePersistZebraEventMutation,
  useRawOrdersQuery,
} from '@/entities/order/model'
import {
  useDigitisedPoaEnabled,
  useStateConfigQuery,
  useTmInitialsEnabled,
  useZebraBarcodeEnabled,
} from '@/entities/state/model'
import {
  ARRIVED_STATUS,
  calculateTotalCrates,
  clearZebraOrderScanState,
  COMPLETED_WITHOUT_SCANNING_MESSAGE,
  DELIVERING_TO_CUSTOMER_STATUS,
  EXPECTED_CRATES_CACHE_TTL_MS,
  getAllCrateTypes,
  getAllCrateTypesFromExpectedCrateItems,
  getDynamicCrateTypesFromExpectedAndScanned,
  getExpectedCrateItemsForZebraOrder,
  getExpectedCrateItemsFromArrayBarcodes,
  getNextIncompleteGroupOrderId,
  getRestrictedCategories,
  getScanAlert,
  getZebraOrderScanState,
  hasReceivedLicencePlates,
  isCompletedWithoutScanning,
  isZebraScanProgressStatus,
  LICENCE_PLATE_WAITING_MESSAGE,
  notifyGroupScanIncomplete,
  parseScanResponse,
  reconcileZebraOfflineScanWithOrdersCache,
  SCAN_ALERT_MESSAGES,
  setZebraScanActiveOrderId,
  shouldDiscardZebraScanState,
  shouldShowLicencePlateWaitingCard,
  showScanAlert,
  toProofOfAgePayload,
  upsertZebraOrderExpectedCrateItems,
  upsertZebraOrderScanState,
  useCollectionCodeGate,
  useOrderDetails,
  useOrderDetailsNavigation,
  useOrderGroupMembers,
  usePoaGate,
  useQuickActions,
  useZebraScanner,
  validateScannedCratesAgainstExpected,
  ZEBRA_ORDER_SCAN_DISCARDED_EVENT,
} from '@/features/order/lib'
import { useRealtimeStore } from '@/features/realtime/model'
import { useResponsiveModal } from '@/shared/lib/mantine'
import headerIconButtonClasses from '@/shared/ui/component-styles/HeaderIconButton.module.css'
import modalClasses from '@/shared/ui/component-styles/Modal.module.css'
import { useCollectionCodeModal } from '../../collection-code-modal'
import { useTmInitialsStore } from '../../initials-capture-modal'
import { PoaFlowPanel } from '../../poa-flow'
import { OrderDetailsDesktopLayout } from '../desktop-layout/OrderDetailsDesktopLayout'
import { OrderDetailsMetaHeader } from '../meta-header'
import { MissingCratesModal } from '../missing-crates/MissingCratesModal'
import { OrderDetailsMobileLayout } from '../mobile-layout/OrderDetailsMobileLayout'
import { OrderSettingsMenu } from '../settings-menu/OrderSettingsMenu'
import { StatusUpdateDrawer } from '../status-drawer/StatusUpdateDrawer'
import { CancelOrderConfirmationModal } from './cancel-confirmation'
import classes from './OrderDetailsModal.module.css'

interface OrderDetailsModalProps {
  orderId: OrderDetails['orderId']
  parentPath: string
  withoutBackdrop?: boolean
  /** BD-8245: render the Proof of Age checks instead of the usual body. */
  isPoaFlow?: boolean
  /** BD-8595: progressive Order Details phase while the onboarding tutorial modal is open. */
  tutorialScanPhase?: import('@/features/onboarding/lib/tutorialScanOrder').TutorialScanPhase | null
}

/**
 * Modal component for displaying order details (Aligns with Coles-specific design)
 */
export function OrderDetailsModal({
  orderId,
  parentPath,
  withoutBackdrop = false,
  isPoaFlow = false,
  tutorialScanPhase = null,
}: OrderDetailsModalProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { order, isLoading } = useOrderDetails(orderId)
  const { updateOrderAcknowledgement } = useAcknowledgeOrder()
  const updateOrderStatus = useOrderStatusMutation()
  const isTmInitialsEnabled = useTmInitialsEnabled()
  const enqueueInitials = useTmInitialsStore(state => state.enqueue)
  const { mutate: persistZebraEvent } = usePersistZebraEventMutation()
  const { data: allOrders } = useRawOrdersQuery()
  const poaEnabled = useDigitisedPoaEnabled()
  const zebraBarcodeEnabled = useZebraBarcodeEnabled()
  const { data: stateConfig } = useStateConfigQuery()
  const { isZebraDevice, start, getScannedList, lastScanData, scanEventCount } = useZebraScanner()
  const { handleCompletionAction, handlePrimaryAction, primaryAction, statusColor, isBlockedByGroupScan } = useQuickActions(order)
  const navigateToOrderDetails = useOrderDetailsNavigation()
  const groupMemberOrderIds = useOrderGroupMembers(orderId)
  const { requiresCollectionCode } = useCollectionCodeGate()
  const { open: openCollectionCodeModal } = useCollectionCodeModal()
  const { shouldGateCompletion } = usePoaGate()
  // The `poa` search param only asks for the checks. A saved or edited URL must not
  // open them once the toggle is off or for an order that does not need them, so
  // the same gate as the completion buttons decides — re-evaluated on every render,
  // so turning the toggle off mid-flow returns to the normal Order Details.
  const showPoaFlow = isPoaFlow && shouldGateCompletion(order)

  const [modalOpened, setModalOpened] = useState(true)
  const modalConfig = useResponsiveModal()
  const [statusDrawerOpened, { open: openStatusDrawer, close: closeStatusDrawer }] = useDisclosure(false)
  const [cancelModalOpened, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false)
  const [selectedCrate, setSelectedCrate] = useState<CrateType | null>(null)
  const [localMissingCrates, setLocalMissingCrates] = useState<MissingCrateItem[]>([])
  const [persistedScannedCrates, setPersistedScannedCrates] = useState<ZebraBarcodesScannedDetails['scannedBarcodes']>([])
  const [cachedExpectedCrateItems, setCachedExpectedCrateItems] = useState<CrateItem[]>([])
  const [cachedExpectedUpdatedAt, setCachedExpectedUpdatedAt] = useState<string | null>(null)
  const sentToBackendBarcodesRef = useRef<Set<string>>(new Set())
  const knownScannedBarcodesRef = useRef<Set<string>>(new Set())
  const sessionMissingMarkCountRef = useRef(0)
  // Per-barcode counts in the native scan list when scanning first became active for this session.
  // The native list is cumulative — each physical scan appends an entry — so a new scan raises
  // the count above the baseline even for a barcode that was already there (e.g. scanned on the
  // dashboard before the modal opened (BD-7982) or scanned into a wrong-order modal (BD-7983)).
  const nativeScanBaselineRef = useRef<Map<string, number> | null>(null)

  const handleClose = () => {
    // Clears every search param including the Proof of Age flag, so backing out
    // records nothing and leaves the order in its current state (BD-8245 AC4).
    setModalOpened(false)
    navigate({
      to: parentPath,
      search: {},
      resetScroll: false,
    })
  }

  const handlePoaComplete = async (submission: PoaSubmission, scannedCrates: CrateItem[]): Promise<PoaCompleteOutcome | void> => {
    // Checked again at submission: a record is never sent for an order the gate no
    // longer covers, even if the toggle changed after the flow was shown.
    if (!order || !shouldGateCompletion(order))
      return 'not-submitted'

    const proofOfAge = toProofOfAgePayload(submission, zebraBarcodeEnabled ? scannedCrates : undefined)
    // BD-8761: when the order also needs a collection code, the checks hand over to
    // the code step instead of completing. The record travels with it and is
    // submitted on the same status patch as the code or exception reason, and the
    // answers are kept until that patch succeeds so a rejected code can be retried.
    // Scoped to this order, as the checks themselves are.
    if (requiresCollectionCode(order)) {
      openCollectionCodeModal({
        orderId: order.orderId,
        orderIds: [order.orderId],
        nextStatus: 'Completed',
        lastUpdateTime: order.lastUpdateTime,
        proofOfAge,
      })
      return 'awaiting-verification'
    }

    // The audit record rides on the status patch itself (BD-8253), so the completion
    // and the record cannot diverge — if the call fails the order is simply not
    // completed and the step can be retried.
    await updateOrderStatus.mutateAsync({
      orderId: order.orderId,
      newStatus: 'Completed',
      lastUpdateTime: order.lastUpdateTime,
      proofOfAge,
    })

    // Initials are then captured by the existing Team Member Initials feature, on its
    // own toggle and through its own queue — exactly as a non-restricted completion
    // does. Proof of Age neither collects them nor depends on that feature being on,
    // and the record deliberately omits them: where the toggle is enabled they are
    // already written to the order as `tm_initials`.
    if (isTmInitialsEnabled) {
      enqueueInitials({ orderId: order.orderId, customerName: order.customerName })
    }

    handleClose()
    return 'completed'
  }

  const handleCancelOrder = async () => {
    if (!order) {
      return
    }

    try {
      await updateOrderStatus.mutateAsync({
        orderId: order.orderId,
        newStatus: 'cancelled',
        lastUpdateTime: order.lastUpdateTime,
      })

      closeCancelModal()
      handleClose()
    } catch (error) {
      console.error('Failed to cancel the order:', error)
    }
  }

  const isCancelledOrder = order?.status?.toLowerCase() === 'cancelled'
  const isActiveScanProgressStatus = zebraBarcodeEnabled
    && isZebraScanProgressStatus(order?.status, stateConfig?.states)
  const expectedCrateItemsFromArrayBarcodes = useMemo(
    () => zebraBarcodeEnabled ? getExpectedCrateItemsFromArrayBarcodes(order) : [],
    [order, zebraBarcodeEnabled],
  )
  const hasArrayBarcodes = expectedCrateItemsFromArrayBarcodes.length > 0
  const showLicencePlateWaitingCard = shouldShowLicencePlateWaitingCard({
    zebraBarcodeEnabled,
    status: order?.status,
    states: stateConfig?.states,
    hasArrayBarcodes,
    isCancelled: isCancelledOrder,
  })
  // The waiting card also applies from Arrived onwards (BD-8588 AC has no state limit), but
  // the action is not a disabled Scan crates there: Arrived offers "Proceed without scanning"
  // (BD-8590) and Delivering keeps "Mark as Completed". Both complete through the BD-8589
  // confirmation; Proceed goes straight to Completed, skipping Delivering to Customer.
  // Not array_barcodes alone: right after Arrived -> Delivering the payload can lack it for
  // a few minutes while the crates are still known (crateScan / the device cache).
  const hasReceivedPlates = useMemo(
    () => hasReceivedLicencePlates(order),
    [order, cachedExpectedUpdatedAt], // eslint-disable-line react-hooks/exhaustive-deps -- cachedExpectedUpdatedAt changes when the device cache the helper reads is written
  )
  const isAwaitingLicencePlatesFromArrived = zebraBarcodeEnabled
    && (order?.status === ARRIVED_STATUS || order?.status === DELIVERING_TO_CUSTOMER_STATUS)
    && !hasReceivedPlates
    && !isCancelledOrder
  const showWaitingCard = showLicencePlateWaitingCard || isAwaitingLicencePlatesFromArrived
  // BD-8595: tutorial phases must show crate progress / CTAs from mock phase data, never the waiting card.
  const tutorialMissingEnabled = Boolean(tutorialScanPhase)
  const effectiveWaitingCard = tutorialMissingEnabled ? false : showWaitingCard
  const isZebraScanActionVisible = isActiveScanProgressStatus && isZebraDevice && !isCancelledOrder
  const canUpdateCrateScan = isZebraScanActionVisible && !effectiveWaitingCard
  // Disabled only for the pre-Arrived waiting card. Arrived uses
  // "Proceed without scanning" (BD-8590) which must stay enabled; tutorial phases
  // already suppress the waiting card via effectiveWaitingCard + array_barcodes mock.
  // BD-8590: every device on a Zebra-enabled destination, not only Zebra handsets, so iOS
  // and desktop cannot complete an unscanned order from here before Arrived either.
  const isScanActionDisabled = showLicencePlateWaitingCard
    && !tutorialMissingEnabled
  const canProceedWithoutScanning = isActiveScanProgressStatus
    && isAwaitingLicencePlatesFromArrived
    && order?.status === ARRIVED_STATUS
  // BD-8590: a completed order that was never scanned shows the spec message, not hsFields crates.
  const showCompletedWithoutScanningCard = isCompletedWithoutScanning(order, zebraBarcodeEnabled)
  const licencePlateCardMessage = showCompletedWithoutScanningCard
    ? COMPLETED_WITHOUT_SCANNING_MESSAGE
    : LICENCE_PLATE_WAITING_MESSAGE
  const restrictedCategories = useMemo(
    () => poaEnabled ? getRestrictedCategories(order) : [],
    [order, poaEnabled],
  )

  useEffect(() => {
    if (!canUpdateCrateScan || !order?.orderId) {
      setZebraScanActiveOrderId(null)
      return
    }
    setZebraScanActiveOrderId(order.orderId)
    return () => setZebraScanActiveOrderId(null)
  }, [canUpdateCrateScan, order?.orderId])

  // BD-8595: drive the Missing Crates sheet from tutorial phases 8–9.
  /* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect -- sync selected crate to tutorial phase */
  useEffect(() => {
    if (tutorialScanPhase === 'missing-sheet' || tutorialScanPhase === 'missing-success') {
      setSelectedCrate({ type: 'Bulk Ambient', count: 1, scannedCount: 0 })
      return
    }
    if (tutorialScanPhase) {
      setSelectedCrate(null)
    }
  }, [tutorialScanPhase])
  /* eslint-enable react-hooks-extra/no-direct-set-state-in-use-effect */

  // On unmount (user navigates back), immediately merge localStorage scan state into both
  // the TanStack polled cache and the Zustand realtime store.
  // useEffectiveOrders returns realtimeOrders when lastRealtimeMs > lastPollMs (i.e. after
  // any WebSocket event), so patching only the TanStack cache is not enough — the dashboard
  // would still show the stale realtime snapshot without the local scan.
  useEffect(() => {
    return () => {
      reconcileZebraOfflineScanWithOrdersCache(queryClient)
      const realtimeState = useRealtimeStore.getState()
      if (realtimeState.realtimeOrders.length > 0) {
        const orders = queryClient.getQueryData<OrderDetails[]>(orderKeys.orders())
        const patchedOrder = orders?.find(o => o.orderId === orderId)
        if (patchedOrder) {
          useRealtimeStore.setState({
            realtimeOrders: realtimeState.realtimeOrders.map(o =>
              o.orderId === orderId ? patchedOrder : o,
            ),
          })
        }
      }
    }
  }, [queryClient, orderId])

  const expectedCrateItemsFromOrder = useMemo(
    () => zebraBarcodeEnabled ? getExpectedCrateItemsForZebraOrder(order) : [],
    [order, zebraBarcodeEnabled],
  )
  const showScanProgress = isActiveScanProgressStatus || (isCancelledOrder && expectedCrateItemsFromArrayBarcodes.length > 0)
  const expectedCrateItems = useMemo(() => {
    if (expectedCrateItemsFromOrder.length > 0 || !showScanProgress || !order) {
      return expectedCrateItemsFromOrder
    }

    if (!cachedExpectedUpdatedAt) {
      return []
    }
    const updatedAtMs = Date.parse(cachedExpectedUpdatedAt)
    if (Number.isNaN(updatedAtMs) || Date.now() - updatedAtMs > EXPECTED_CRATES_CACHE_TTL_MS) {
      return []
    }
    if (cachedExpectedCrateItems.length === 0) {
      return []
    }

    return cachedExpectedCrateItems
  }, [cachedExpectedCrateItems, cachedExpectedUpdatedAt, expectedCrateItemsFromOrder, order, showScanProgress])
  const validatedScannedCrates = useMemo(
    () => {
      if (!order || !canUpdateCrateScan) {
        nativeScanBaselineRef.current = null
        return []
      }
      // Re-run when native layer reports a scan; getScannedList reads device state (not a React dep).
      void lastScanData
      // The native list is keyed by the licence plate's A-prefixed order segment.
      const allScanned = getScannedList(`A${order.orderId}`)
      if (nativeScanBaselineRef.current === null) {
        // First scan-capable render: count each barcode already in the native list as pre-session.
        const baseline = new Map<string, number>()
        allScanned.forEach((s) => {
          const key = `${s.orderId}*${s.areaCode}*${s.crateNumber}`
          baseline.set(key, (baseline.get(key) ?? 0) + 1)
        })
        nativeScanBaselineRef.current = baseline
        return []
      }
      const currentCounts = new Map<string, number>()
      allScanned.forEach((s) => {
        const key = `${s.orderId}*${s.areaCode}*${s.crateNumber}`
        currentCounts.set(key, (currentCounts.get(key) ?? 0) + 1)
      })
      const sessionScans = allScanned.filter((s) => {
        const key = `${s.orderId}*${s.areaCode}*${s.crateNumber}`
        return (currentCounts.get(key) ?? 0) > (nativeScanBaselineRef.current!.get(key) ?? 0)
      })
      return validateScannedCratesAgainstExpected(expectedCrateItems, sessionScans)
    },
    [canUpdateCrateScan, expectedCrateItems, getScannedList, lastScanData, order],
  )
  const mergedScannedCrates = useMemo(() => {
    const byBarcode = new Map<string, ZebraBarcodesScannedDetails['scannedBarcodes'][number]>()
      ;[
      ...(order?.crateScan?.scannedBarcodes ?? []),
      ...persistedScannedCrates,
      ...validatedScannedCrates,
    ].forEach((item) => {
      byBarcode.set(item.barcode, item)
    })
    return [...byBarcode.values()]
  }, [order?.crateScan?.scannedBarcodes, persistedScannedCrates, validatedScannedCrates])
  // BD-8719: progress counts only this order's expected crates, as the order cards do
  // (isOrderAccountedForGroupScan). The device's saved scans for an order can carry a
  // grouped partner's crate; counted, it finished this order early and showed "Go to
  // next grouped order" while the card still said "Continue scanning".
  const orderScannedCrates = useMemo(() => {
    const expectedBarcodes = new Set(expectedCrateItems.map(item => item.barcode))
    return mergedScannedCrates.filter(item => expectedBarcodes.has(item.barcode))
  }, [expectedCrateItems, mergedScannedCrates])
  const crateTypesFromOrder: CrateType[] = useMemo(() => {
    if (!order) {
      return []
    }
    if (showScanProgress) {
      return getDynamicCrateTypesFromExpectedAndScanned(expectedCrateItems, orderScannedCrates)
    }
    if (expectedCrateItemsFromArrayBarcodes.length > 0) {
      return getAllCrateTypesFromExpectedCrateItems(
        expectedCrateItemsFromArrayBarcodes,
        order.areaCodeCrateTypeMapping,
        order.hsFields,
      )
    }
    return getAllCrateTypes(order.hsFields)
  }, [expectedCrateItems, expectedCrateItemsFromArrayBarcodes, orderScannedCrates, order, showScanProgress])
  const expectedCountByType = useMemo(() => {
    const countByType = new Map<string, number>()
    expectedCrateItems.forEach((item) => {
      countByType.set(item.crateType, (countByType.get(item.crateType) ?? 0) + 1)
    })
    return countByType
  }, [expectedCrateItems])
  const scannedCountByType = useMemo(() => {
    const countByType = new Map<string, number>()
    orderScannedCrates.forEach((item) => {
      countByType.set(item.crateType, (countByType.get(item.crateType) ?? 0) + 1)
    })
    return countByType
  }, [orderScannedCrates])
  const missingCrateTypes = useMemo(() => {
    if (!showScanProgress) {
      return []
    }
    const fromBackend = order?.crateScan?.missingCrates.map(c => c.crateType) ?? []
    const fromLocal = localMissingCrates.map(c => c.crateType)
    const combined = [...new Set([...fromBackend, ...fromLocal])]
    // Only render as missing if there are still unscanned crates of that type.
    // Guards against stale localMissingCrates when all crates are later scanned.
    return combined.filter((type) => {
      const scanned = scannedCountByType.get(type) ?? 0
      const expected = expectedCountByType.get(type) ?? 0
      return scanned < expected
    })
  }, [expectedCountByType, localMissingCrates, order?.crateScan?.missingCrates, scannedCountByType, showScanProgress])
  const unresolvedMissingCrates = useMemo(() => {
    if (!showScanProgress) {
      return []
    }
    const combinedMissing = [...(order?.crateScan?.missingCrates ?? []), ...localMissingCrates]
    const dedupedByType = new Map<string, MissingCrateItem>()
    combinedMissing.forEach((item) => {
      const expected = expectedCountByType.get(item.crateType) ?? 0
      const scanned = scannedCountByType.get(item.crateType) ?? 0
      if (scanned < expected) {
        dedupedByType.set(item.crateType, item)
      }
    })
    return [...dedupedByType.values()]
  }, [expectedCountByType, localMissingCrates, order?.crateScan?.missingCrates, scannedCountByType, showScanProgress])

  const crateTypes = useMemo(() => {
    // BD-8625: with Zebra off, keep the fixed hsFields crate rows (zeros included) exactly as
    // production shows them; zero-count rows are hidden only on Zebra-enabled destinations.
    const visibleCrateTypes = zebraBarcodeEnabled
      ? crateTypesFromOrder.filter(crate => crate.count > 0)
      : crateTypesFromOrder

    if (!isCancelledOrder || !showScanProgress) {
      return visibleCrateTypes
    }

    return visibleCrateTypes.map((crate) => {
      if (!missingCrateTypes.includes(crate.type)) {
        return crate
      }

      const expected = expectedCountByType.get(crate.type) ?? crate.count
      return {
        ...crate,
        scannedCount: Math.max(crate.scannedCount ?? 0, expected),
      }
    })
  }, [crateTypesFromOrder, expectedCountByType, isCancelledOrder, missingCrateTypes, showScanProgress, zebraBarcodeEnabled])
  const totalCrates = calculateTotalCrates(crateTypes)
  const unresolvedMissingCount = useMemo(() => unresolvedMissingCrates.reduce((sum, item) => {
    const expected = expectedCountByType.get(item.crateType) ?? 0
    const scanned = scannedCountByType.get(item.crateType) ?? 0
    return sum + Math.max(0, expected - scanned)
  }, 0), [expectedCountByType, scannedCountByType, unresolvedMissingCrates])
  const scannedCount = useMemo(() => {
    if (!showScanProgress) {
      return 0
    }
    return orderScannedCrates.length + (isCancelledOrder ? unresolvedMissingCount : 0)
  }, [isCancelledOrder, orderScannedCrates.length, showScanProgress, unresolvedMissingCount])

  const handleMissingCrateMarked = useCallback((item: MissingCrateItem) => {
    if (!canUpdateCrateScan) {
      return
    }

    // Marking a crate missing accounts for it exactly as scanning it does, so it counts
    // towards "the team member finished this order here" in the same way.
    sessionMissingMarkCountRef.current += 1
    setLocalMissingCrates((prev) => {
      const nextMissingCrates = [...prev.filter(c => c.crateType !== item.crateType), item]
      // Tutorial phases must not pollute device LS (dashboard would show Scanned 7/7 on restart).
      if (order?.orderId && !globalThis.window.__tutorialMode) {
        upsertZebraOrderScanState(order.orderId, {
          scannedBarcodes: persistedScannedCrates,
          missingCrates: nextMissingCrates,
        })
      }
      return nextMissingCrates
    })
    if (order?.orderId) {
      const areaCode = expectedCrateItems.find(
        i => i.crateType.trim().toLowerCase() === item.crateType.trim().toLowerCase(),
      )?.areaCode ?? ''
      persistZebraEvent({
        eventType: 'missingCrates',
        orderId: order.orderId,
        crateType: item.crateType,
        areaCode,
        reason: item.reason,
        comment: item.comment,
        deviceFingerprint: order.crateScan?.device?.fingerprint,
        timestamp: new Date().toISOString(),
      })
    }
  }, [canUpdateCrateScan, expectedCrateItems, order?.orderId, order?.crateScan?.device?.fingerprint, persistedScannedCrates, persistZebraEvent])

  /* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect -- hydrate / persist Zebra state from localStorage and lifecycle */
  useEffect(() => {
    if (!order?.orderId) {
      return
    }
    const persistedState = getZebraOrderScanState(order.orderId)
    if (!persistedState) {
      setLocalMissingCrates([])
      setPersistedScannedCrates([])
      setCachedExpectedCrateItems([])
      setCachedExpectedUpdatedAt(null)
      return
    }
    setLocalMissingCrates(persistedState.missingCrates ?? [])
    setPersistedScannedCrates(persistedState.scannedBarcodes ?? [])
    setCachedExpectedCrateItems(persistedState.expectedCrateItems ?? [])
    setCachedExpectedUpdatedAt(persistedState.updatedAt ?? null)
  }, [order?.orderId])

  useEffect(() => {
    if (!showScanProgress || !order?.orderId || expectedCrateItemsFromOrder.length === 0) {
      return
    }
    const isSameCache = expectedCrateItemsFromOrder.length === cachedExpectedCrateItems.length
      && expectedCrateItemsFromOrder.every((item, index) => (
        item.barcode === cachedExpectedCrateItems[index]?.barcode
        && item.crateType === cachedExpectedCrateItems[index]?.crateType
      ))
    if (isSameCache) {
      return
    }
    setCachedExpectedCrateItems(expectedCrateItemsFromOrder)
    setCachedExpectedUpdatedAt(new Date().toISOString())
    if (!globalThis.window.__tutorialMode) {
      upsertZebraOrderExpectedCrateItems(order.orderId, expectedCrateItemsFromOrder)
    }
  }, [cachedExpectedCrateItems, expectedCrateItemsFromOrder, order?.orderId, showScanProgress])

  useEffect(() => {
    const handler = (e: Event) => {
      if (!(e instanceof CustomEvent)) {
        return
      }
      if ((e.detail as { orderId?: string } | null)?.orderId !== orderId) {
        return
      }
      setLocalMissingCrates([])
      setPersistedScannedCrates([])
      setCachedExpectedCrateItems([])
      setCachedExpectedUpdatedAt(null)
    }
    window.addEventListener(ZEBRA_ORDER_SCAN_DISCARDED_EVENT, handler)
    return () => window.removeEventListener(ZEBRA_ORDER_SCAN_DISCARDED_EVENT, handler)
  }, [orderId])

  useEffect(() => {
    if (!canUpdateCrateScan || !order?.orderId) {
      return
    }

    if (!globalThis.window.__tutorialMode) {
      upsertZebraOrderScanState(order.orderId, {
        scannedBarcodes: mergedScannedCrates,
        missingCrates: localMissingCrates,
      })
    }
    const sameScannedCrates = persistedScannedCrates.length === mergedScannedCrates.length
      && persistedScannedCrates.every((item, index) => item.barcode === mergedScannedCrates[index]?.barcode)
    if (!sameScannedCrates) {
      setPersistedScannedCrates(mergedScannedCrates)
    }
  }, [canUpdateCrateScan, localMissingCrates, mergedScannedCrates, order?.orderId, persistedScannedCrates])

  useEffect(() => {
    if (!order?.orderId || !stateConfig?.states) {
      return
    }

    if (!shouldDiscardZebraScanState(order.status, stateConfig.states)) {
      return
    }

    clearZebraOrderScanState(order.orderId)
    setPersistedScannedCrates([])
    setLocalMissingCrates([])
    setCachedExpectedCrateItems([])
    setCachedExpectedUpdatedAt(null)
  }, [order?.orderId, order?.status, stateConfig?.states])
  /* eslint-enable react-hooks-extra/no-direct-set-state-in-use-effect */

  useEffect(() => {
    if (!canUpdateCrateScan || !order?.orderId) {
      sentToBackendBarcodesRef.current = new Set()
      return
    }
    const { orderId } = order
    const fingerprint = order.crateScan?.device?.fingerprint
    for (const crateItem of validatedScannedCrates) {
      if (!sentToBackendBarcodesRef.current.has(crateItem.barcode)) {
        sentToBackendBarcodesRef.current.add(crateItem.barcode)
        persistZebraEvent({
          eventType: 'zebraScans',
          orderId,
          barcode: crateItem.barcode,
          areaCode: crateItem.areaCode,
          crateNumber: crateItem.crateNumber,
          crateType: crateItem.crateType,
          deviceFingerprint: fingerprint,
          timestamp: new Date().toISOString(),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persistZebraEvent is stable; order fields read directly to avoid stale closure on frequent order refreshes
  }, [canUpdateCrateScan, validatedScannedCrates, order?.orderId])

  // Barcodes accounted for before the current scan event. Seeded when the order opens,
  // so a crate scanned in an earlier session still reads as a duplicate, then advanced
  // one scan at a time. `mergedScannedCrates` cannot answer this: by the time the scan
  // effect runs it already contains the barcode that has just arrived.
  useEffect(() => {
    knownScannedBarcodesRef.current = new Set(mergedScannedCrates.map(item => item.barcode))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed per order only; within an order the ref advances per scan event
  }, [order?.orderId])

  useEffect(() => {
    if (!canUpdateCrateScan || !lastScanData || !order?.orderId) {
      return
    }
    const parsed = parseScanResponse(lastScanData)
    if (!parsed?.success || !parsed.orderDetail) {
      return
    }
    const { orderId: scannedOrderId, areaCode, crateNumber } = parsed.orderDetail
    // The scanned orderId is the licence plate's A-prefixed segment.
    if (scannedOrderId.trim() !== `A${order.orderId.trim()}`) {
      return
    }
    const expectedBarcodeSet = new Set(expectedCrateItems.map(item => item.barcode))
    const candidateBarcode = `${scannedOrderId.trim()}*${areaCode}*${crateNumber}`
    // BD-8396: re-scanning an accounted-for crate used to do nothing at all - no sound,
    // no message, no counter movement - which looks exactly like the scanner failing to
    // read, so the team member scans again. It is not an error and changes no state, so
    // it gets the same neutral treatment as every other scan alert (BD-8393).
    const scanAlert = getScanAlert(candidateBarcode, expectedBarcodeSet, knownScannedBarcodesRef.current)
    if (scanAlert !== 'wrong-scan') {
      knownScannedBarcodesRef.current.add(candidateBarcode)
    }
    if (scanAlert) {
      showScanAlert({ message: SCAN_ALERT_MESSAGES[scanAlert] })
    }
  }, [scanEventCount]) // eslint-disable-line react-hooks/exhaustive-deps -- scanEventCount increments on every scan event; other deps are stable refs read at scan time

  const accountedCrateCount = orderScannedCrates.length + unresolvedMissingCount
  // BD-8588: empty array_barcodes must not flip to completionReady while waiting for licence plates
  const completionReady = showScanProgress
    && !showLicencePlateWaitingCard
    && (totalCrates === 0 || accountedCrateCount >= totalCrates)
  const isArrivedAllProcessed = completionReady && totalCrates > 0 && order?.status === ARRIVED_STATUS
  const isDeliveringAllProcessed = completionReady
    && (totalCrates === 0 || order?.status === DELIVERING_TO_CUSTOMER_STATUS)

  const groupMemberOrders = useMemo(
    () => (allOrders ?? []).filter(member => groupMemberOrderIds.includes(member.orderId)),
    [allOrders, groupMemberOrderIds],
  )
  const nextGroupedOrderId = useMemo(
    () => getNextIncompleteGroupOrderId(groupMemberOrders, orderId),
    [groupMemberOrders, orderId],
  )
  // BD-8185: this member is done but the partner still has crates — offer / auto-advance
  // instead of the silent "We're on it" no-op from the BD-8186 gate.
  //
  // Scenario 3 is about this order's crates being finished, not about its status. A group
  // is routinely still OnTheWay while its crates are scanned — that is the state the
  // group card's "Scan x/y" button opens from — so requiring Arrived here left the team
  // member with no way forward until the status happened to catch up.
  const thisOrderFullyScanned = completionReady && totalCrates > 0
  const shouldGoToNextGroupedOrder = Boolean(
    thisOrderFullyScanned && isBlockedByGroupScan && nextGroupedOrderId,
  )
  const goToNextGroupedOrderLabel = groupMemberOrderIds.length > 1
    ? `Go to next grouped order (${groupMemberOrderIds.length})`
    : 'Go to next grouped order'

  // Auto-advance only when this session finishes the last crate (Scenario 3), not when
  // opening an already-complete member (Scenario 5 — show the button, user can tap).
  const isFirstScanActionPaintRef = useRef(true)
  const prevCompletionReadyRef = useRef(false)
  const didAutoAdvanceRef = useRef(false)
  const pendingAutoAdvanceRef = useRef(false)
  useEffect(() => {
    isFirstScanActionPaintRef.current = true
    prevCompletionReadyRef.current = false
    didAutoAdvanceRef.current = false
    pendingAutoAdvanceRef.current = false
    sessionMissingMarkCountRef.current = 0
  }, [orderId])

  useEffect(() => {
    if (isFirstScanActionPaintRef.current) {
      isFirstScanActionPaintRef.current = false
      prevCompletionReadyRef.current = completionReady
      return
    }

    // Finishing the last crate raises the intent to advance; acting on it needs the
    // group's own state, which can settle a render or two later. Latch the intent
    // rather than reading it as a single moment, or it is gone by the time the rest
    // catches up. Cleared if the order stops being complete, so a corrected scan does
    // not advance later on.
    //
    // What separates Scenario 3 from Scenario 5 is whether the team member accounted
    // for a crate here, by scanning it or by marking it missing — both counted since
    // this modal opened. Reopening an order that was already finished can also flip
    // completion, because locally recorded scans arrive a render after the first paint,
    // and that must show the button without jumping anywhere.
    const accountedForSomethingHere = scanEventCount > 0 || sessionMissingMarkCountRef.current > 0
    if (completionReady && !prevCompletionReadyRef.current && accountedForSomethingHere) {
      pendingAutoAdvanceRef.current = true
    }
    prevCompletionReadyRef.current = completionReady
    if (!completionReady) {
      pendingAutoAdvanceRef.current = false
    }

    if (!pendingAutoAdvanceRef.current || !shouldGoToNextGroupedOrder || !nextGroupedOrderId || didAutoAdvanceRef.current) {
      return
    }
    didAutoAdvanceRef.current = true
    pendingAutoAdvanceRef.current = false
    navigateToOrderDetails(nextGroupedOrderId)
  }, [completionReady, scanEventCount, shouldGoToNextGroupedOrder, nextGroupedOrderId, navigateToOrderDetails])

  // When all crates are processed, button text/action follow order status on every device
  // (Zebra and non-Zebra). Do not force Complete just because scanning is unavailable.
  // On The Way → "Scanned X/Y"; Arrived → primary quick action ("We're on it"); Delivering → Complete.
  // BD-8185: Arrived + partner still outstanding → "Go to next grouped order".
  // BD-8588: waiting for licence plates → "Scan crates" visible but disabled on Zebra.
  // BD-8590: Arrived without licence plates → "Proceed without scanning".
  // BD-8595: tutorial phases show scan CTAs from mock data even without a Zebra device.
  const effectiveShowScanProgress = showScanProgress || tutorialMissingEnabled
  // BD-8130: the primary action moves the whole group, so it carries the group count like the group card.
  const primaryActionLabel = primaryAction && groupMemberOrderIds.length > 1
    ? `${primaryAction.label} (${groupMemberOrderIds.length})`
    : primaryAction?.label
  const scanActionLabel = !effectiveShowScanProgress || isCancelledOrder
    ? 'Update status'
    : canProceedWithoutScanning
      ? 'Proceed without scanning'
      : isScanActionDisabled
        ? 'Scan crates'
        : !completionReady
            ? (canUpdateCrateScan || tutorialMissingEnabled
                ? (scannedCount > 0
                    ? `Continue scanning ${scannedCount}/${totalCrates}`
                  // Progress counts live in the Total crates header (Figma: "Scan crates").
                    : 'Scan crates')
                : 'Mark as Completed')
            : shouldGoToNextGroupedOrder
              ? goToNextGroupedOrderLabel
              : isArrivedAllProcessed && primaryAction
              // BD-8595 Step 10: teaching CTA matches the tip copy (not "We're on it").
                ? (tutorialScanPhase === 'all-accounted' || tutorialScanPhase === 'order-details-status'
                    ? 'Delivering to customer'
                    : primaryActionLabel)
                : isDeliveringAllProcessed
                  ? 'Mark as Completed'
                  : `Scanned ${totalCrates}/${totalCrates}`
  const completeOrder = () => {
    // BD-8641: the quick action stays silent for the card's BD-8185 case, but this CTA reads
    // "Mark as Completed" on iOS while the group still has crates out — say why, as the status drawer does.
    if (isBlockedByGroupScan) {
      notifyGroupScanIncomplete()
      return
    }
    void handleCompletionAction()
  }
  const handleScanAction = (() => {
    if (isScanActionDisabled) {
      return undefined
    }
    if (!showScanProgress || isCancelledOrder) {
      return openStatusDrawer
    }
    if (canProceedWithoutScanning) {
      return completeOrder
    }
    if (!completionReady) {
      return canUpdateCrateScan ? start : completeOrder
    }
    if (shouldGoToNextGroupedOrder && nextGroupedOrderId) {
      return () => {
        navigateToOrderDetails(nextGroupedOrderId)
      }
    }
    if (isArrivedAllProcessed && primaryAction) {
      return () => {
        void handlePrimaryAction()
      }
    }
    if (isDeliveringAllProcessed) {
      return completeOrder
    }
    // On The Way (and other pre-delivering statuses): scanning done — open status drawer
    return openStatusDrawer
  })()

  useEffect(() => {
    if (order && !globalThis.window.__tutorialMode) {
      const acknowledgeAllOrders = async () => {
        try {
          const orderMap = allOrders?.reduce((acc: Record<string, OrderDetails>, o: OrderDetails) => {
            acc[o.orderId] = o
            return acc
          }, {} as Record<string, OrderDetails>) || {}

          const acknowledgmentPromises = groupMemberOrderIds.map((id) => {
            const orderToUse = orderMap[id] || {
              orderId: id,
              status: order.status,
              isAcknowledged: false,
              lastUpdateTime: order.lastUpdateTime,
            }
            return updateOrderAcknowledgement(orderToUse as OrderDetails)
          })

          await Promise.all(acknowledgmentPromises)
        } catch (error) {
          console.error('Failed to acknowledge orders in group:', error)
        }
      }

      acknowledgeAllOrders()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  return (
    <Modal
      opened={modalOpened}
      onClose={handleClose}
      title={null}
      size={modalConfig.size}
      fullScreen={modalConfig.fullScreen}
      transitionProps={modalConfig.transitionProps}
      classNames={{
        content: modalClasses.modalContent,
        body: modalClasses.modalBody,
      }}
      closeOnClickOutside={!withoutBackdrop}
      overlayProps={withoutBackdrop ? { backgroundOpacity: 0, blur: 0 } : undefined}
      data-onboarding="order-details-modal"
    >
      {order && (
        <div className={clsx(
          modalClasses.modalHeader,
          !modalConfig.isSmUp
            ? modalClasses.mobileHeader
            : modalClasses.desktopHeader,
        )}
        >
          {!modalConfig.isSmUp ? (
            <>
              <button
                type="button"
                aria-label="Go back"
                className={headerIconButtonClasses.iconButton}
                onClick={handleClose}
              >
                <div className={headerIconButtonClasses.iconButtonInner}>
                  <IconChevronLeft
                    className={headerIconButtonClasses.icon}
                    size={32}
                  />
                </div>
              </button>

              <h3 className={modalClasses.headerTitle}>{order.customerName}</h3>

              <OrderSettingsMenu
                renderTrigger={({ onClick: _onClick }) => (
                  <button
                    type="button"
                    aria-label="Settings"
                    className={headerIconButtonClasses.iconButton}
                    onClick={_onClick}
                  >
                    <div className={headerIconButtonClasses.iconButtonInner}>
                      <IconSettings
                        className={headerIconButtonClasses.icon}
                        size={32}
                      />
                    </div>
                  </button>
                )}
                onCancelOrder={openCancelModal}
              />
            </>
          ) : (
            <>
              {withoutBackdrop ? (
                <>
                  <button
                    type="button"
                    aria-label="Close modal"
                    className={headerIconButtonClasses.iconButton}
                    onClick={handleClose}
                  >
                    <div className={headerIconButtonClasses.iconButtonInner}>
                      <IconChevronLeft
                        className={headerIconButtonClasses.icon}
                        size={32}
                      />
                    </div>
                  </button>

                  <h3 className={modalClasses.headerTitle}>{order.customerName}</h3>
                </>
              ) : (
                <>
                  <h3 className={modalClasses.headerTitle}>{order.customerName}</h3>

                  <button
                    type="button"
                    aria-label="Close modal"
                    className={headerIconButtonClasses.iconButton}
                    onClick={handleClose}
                  >
                    <div className={headerIconButtonClasses.iconButtonInner}>
                      <IconX
                        className={headerIconButtonClasses.icon}
                        size={32}
                      />
                    </div>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* BD-8244 AC1 puts the restricted-item indicator on the Order Details header
          without restricting it to mobile, so it renders at every width. */}
      {order && restrictedCategories.length > 0 && (
        <OrderDetailsMetaHeader
          order={order}
          restrictedCategories={restrictedCategories}
        />
      )}

      {isLoading ? (
        <Center h={300}>
          <LoadingOverlay visible />
          <Text span c="dimmed">Loading order details...</Text>
        </Center>
      ) : !order ? (
        <Center h={300}>
          <Stack gap="md" align="center">
            <Text span c="dimmed">Could not find order details</Text>
            <Button onClick={handleClose} size="sm">Close</Button>
          </Stack>
        </Center>
      ) : (
        <>
          <Box
            className={clsx(modalClasses.content, showPoaFlow && modalClasses.contentFlush)}
            data-onboarding="order-details-modal-content"
          >
            {showPoaFlow ? (
              <PoaFlowPanel order={order} onComplete={submission => handlePoaComplete(submission, orderScannedCrates)} />
            ) : !modalConfig.isSmUp ? (
              <OrderDetailsMobileLayout
                order={order}
                crateTypes={crateTypes}
                totalCrates={totalCrates}
                scannedCrates={scannedCount}
                withoutBackdrop={withoutBackdrop}
                showScanProgress={showScanProgress || tutorialMissingEnabled}
                showLicencePlateWaitingCard={effectiveWaitingCard || showCompletedWithoutScanningCard}
                licencePlateWaitingMessage={licencePlateCardMessage}
                enableMissingMarking={canUpdateCrateScan || tutorialMissingEnabled}
                missingCrateTypes={missingCrateTypes}
                onCrateClick={canUpdateCrateScan || tutorialMissingEnabled ? setSelectedCrate : undefined}
                onStatusPress={(showScanProgress || tutorialMissingEnabled) && !isCancelledOrder ? openStatusDrawer : undefined}
                statusDrawerOpened={statusDrawerOpened}
              />
            ) : (
              <OrderDetailsDesktopLayout
                order={order}
                crateTypes={crateTypes}
                totalCrates={totalCrates}
                withoutBackdrop={withoutBackdrop}
                scannedCrates={scannedCount}
                showScanProgress={showScanProgress || tutorialMissingEnabled}
                showLicencePlateWaitingCard={effectiveWaitingCard || showCompletedWithoutScanningCard}
                licencePlateWaitingMessage={licencePlateCardMessage}
                missingCrateTypes={missingCrateTypes}
                enableMissingMarking={tutorialMissingEnabled}
              />
            )}
          </Box>

          {!showPoaFlow && (
            <Box className={classes.actions}>
              {!modalConfig.isSmUp ? (
                <Button
                  variant={showScanProgress || tutorialMissingEnabled ? 'outline' : 'filled'}
                  // BD-8590: Proceed without scanning is dark in the design, not the status colour.
                  color={(showScanProgress || tutorialMissingEnabled) && !canProceedWithoutScanning ? (statusColor || 'violet') : 'gray.8'}
                  fullWidth
                  size="lg"
                  radius={showScanProgress || tutorialMissingEnabled ? 'xl' : 'md'}
                  className={showScanProgress || tutorialMissingEnabled ? classes.scanActionButton : undefined}
                  leftSection={
                    (tutorialMissingEnabled && !completionReady)
                    || (canUpdateCrateScan && !completionReady && !canProceedWithoutScanning)
                    || isScanActionDisabled
                      ? <IconQrcode size={20} />
                      : undefined
                  }
                  onClick={handleScanAction}
                  disabled={isScanActionDisabled}
                  data-onboarding="update-status-button"
                >
                  {scanActionLabel}
                </Button>
              ) : (
                <Flex justify="space-between" align="center" w="100%" gap="md">
                  <OrderSettingsMenu
                    renderTrigger={({ onClick: _onClick }) => (
                      <Button
                        variant="subtle"
                        color="gray.8"
                        size="md"
                        radius="md"
                        leftSection={<IconSettings size={24} />}
                        onClick={_onClick}
                      >
                        Options
                      </Button>
                    )}
                    onCancelOrder={openCancelModal}
                    position="top-start"
                  />
                  <Button
                    variant={tutorialMissingEnabled ? 'outline' : 'filled'}
                    color={tutorialMissingEnabled ? (statusColor || 'violet') : 'gray.8'}
                    size={tutorialMissingEnabled ? 'lg' : 'md'}
                    radius={tutorialMissingEnabled ? 'xl' : 'md'}
                    className={tutorialMissingEnabled
                      ? `${classes.scanActionButton} ${classes.actionsDesktopScan}`
                      : classes.actionsDesktop}
                    // BD-8590: desktop has no scan footer, so Proceed without scanning takes the Done slot.
                    onClick={canProceedWithoutScanning && !tutorialMissingEnabled ? completeOrder : handleClose}
                    data-onboarding="update-status-button"
                    leftSection={tutorialMissingEnabled && !completionReady ? <IconQrcode size={20} /> : undefined}
                  >
                    {tutorialMissingEnabled
                      ? scanActionLabel
                      : canProceedWithoutScanning ? 'Proceed without scanning' : 'Done'}
                  </Button>
                </Flex>
              )}
            </Box>
          )}
        </>
      )}

      {/* Status Update Drawer - Only for single column layout */}
      {!showPoaFlow && !modalConfig.isSmUp && order && (
        <StatusUpdateDrawer
          opened={statusDrawerOpened}
          onClose={closeStatusDrawer}
          orderId={order.orderId}
          currentStatus={order.status}
          lastUpdateTime={order.lastUpdateTime}
        />
      )}

      {order && (
        <CancelOrderConfirmationModal
          opened={cancelModalOpened}
          onClose={closeCancelModal}
          onConfirm={handleCancelOrder}
          customerName={order.customerName || 'Unknown Customer'}
          isLoading={updateOrderStatus.isPending}
        />
      )}

      {order && selectedCrate && (canUpdateCrateScan || tutorialMissingEnabled) && (
        <MissingCratesModal
          opened={!!selectedCrate}
          onClose={() => setSelectedCrate(null)}
          crate={selectedCrate}
          orderId={order.orderId}
          onMissingCrateMarked={handleMissingCrateMarked}
          submittedCrate={unresolvedMissingCrates.find(c => c.crateType === selectedCrate.type)}
          tutorialPreset={
            tutorialScanPhase === 'missing-sheet'
              ? { reason: 'Crate merged', succeeded: false }
              : tutorialScanPhase === 'missing-success'
                ? { reason: 'Crate merged', succeeded: true }
                : undefined
          }
        />
      )}
    </Modal>
  )
}
